import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

export default function AdminHub() {
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState({});
  const [edits, setEdits] = useState({});
  const [grantUserId, setGrantUserId] = useState('');
  const [grantAmount, setGrantAmount] = useState(100);
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantError, setGrantError] = useState('');
  const [grantSuccess, setGrantSuccess] = useState('');

  async function loadUsers() {
    try {
      const data = await api.listUsers();
      if (data?.error) throw new Error(data.error);
      const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      setUsers(items);
    } catch (e) {
      setError(e.message || 'Failed to load users');
    }
  }

  async function loadPlans() {
    try {
      const data = await api.listPlans();
      if (data?.error) throw new Error(data.error);
      const items = Array.isArray(data?.items) ? data.items : [];
      setPlans(items);
    } catch (e) {
      console.error('Failed to load plans:', e);
    }
  }

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadUsers(), loadPlans()]);
      setLoading(false);
    })();
  }, []);

  async function handleGrantCredits(e) {
    e.preventDefault();
    setGrantError('');
    setGrantSuccess('');
    if (!grantUserId) {
      setGrantError('Select a user to grant credits');
      return;
    }
    const amountNum = Number(grantAmount || 0);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setGrantError('Enter a positive credit amount');
      return;
    }
    try {
      setGrantBusy(true);
      const res = await api.adminGrantCredits({ userId: grantUserId, amount: amountNum });
      if (res?.error) {
        setGrantError(res.error === 'forbidden' ? 'Not authorized to grant credits' : res.error);
        return;
      }
      const balance = typeof res?.balance === 'number' ? res.balance : null;
      setGrantSuccess(balance !== null ? `Granted ${amountNum} credits. New balance: ${balance}` : `Granted ${amountNum} credits.`);
    } catch (e2) {
      setGrantError(e2?.message || 'Failed to grant credits');
    } finally {
      setGrantBusy(false);
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ color: '#ff7a45', marginBottom: 8 }}>Admin Dashboard</h2>
      <p style={{ color: '#9ca3af', marginBottom: 24 }}>Manage users, subscriptions, uploads, and system settings.</p>

      {/* Quick Stats */}
      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 24 }}>
        <div style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
          <div style={{ color: '#9ca3af', fontSize: 14 }}>Total Users</div>
          <div style={{ color: '#e5e7eb', fontSize: 32, fontWeight: 'bold' }}>{users.length}</div>
        </div>
        <div style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
          <div style={{ color: '#9ca3af', fontSize: 14 }}>Active Plans</div>
          <div style={{ color: '#e5e7eb', fontSize: 32, fontWeight: 'bold' }}>{plans.filter(p => p.active).length}</div>
        </div>
        <div style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
          <div style={{ color: '#9ca3af', fontSize: 14 }}>Admin Users</div>
          <div style={{ color: '#e5e7eb', fontSize: 32, fontWeight: 'bold' }}>{users.filter(u => Array.isArray(u.roles) && u.roles.includes('admin')).length}</div>
        </div>
      </div>

      {/* User Management Card */}
      <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: '#e5e7eb' }}>User Management</h3>
          <Link to="/users" style={{ color: '#ff7a45', fontSize: 14 }}>View All →</Link>
        </div>
        {loading ? (
          <p style={{ color: '#9ca3af' }}>Loading users...</p>
        ) : error ? (
          <p style={{ color: '#fca5a5' }}>{error}</p>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #374151' }}>
                  <th style={{ textAlign: 'left', padding: '8px 0', color: '#9ca3af', fontWeight: 500 }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '8px 0', color: '#9ca3af', fontWeight: 500 }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '8px 0', color: '#9ca3af', fontWeight: 500 }}>Roles</th>
                  <th style={{ textAlign: 'left', padding: '8px 0', color: '#9ca3af', fontWeight: 500 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.slice(0, 10).map(u => {
                  const e = edits[u._id] || { name: u.name || '', avatarUrl: u.avatarUrl || '', roles: Array.isArray(u.roles) ? u.roles : ['user'] };
                  const hasAdmin = Array.isArray(e.roles) && e.roles.includes('admin');
                  return (
                    <tr key={u._id} style={{ borderBottom: '1px solid #1f2937' }}>
                      <td style={{ padding: '12px 0', color: '#e5e7eb' }}>{u.email}</td>
                      <td style={{ padding: '12px 0', color: '#9ca3af' }}>{u.name || '—'}</td>
                      <td style={{ padding: '12px 0' }}>
                        {hasAdmin && <span style={{ background: '#10b981', color: '#0b0b0e', padding: '2px 8px', borderRadius: 6, fontSize: 12, marginRight: 4 }}>Admin</span>}
                        <span style={{ color: '#9ca3af', fontSize: 12 }}>User</span>
                      </td>
                      <td style={{ padding: '12px 0' }}>
                        <Link to={`/users/${u._id}`} style={{ color: '#60a5fa', fontSize: 14 }}>Edit</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {users.length === 0 && <p style={{ color: '#9ca3af', textAlign: 'center', padding: 16 }}>No users found.</p>}
          </div>
        )}
      </section>

      {/* Subscription Plans Card */}
      <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: '#e5e7eb' }}>Subscription Plans</h3>
          <Link to="/admin/subscriptions" style={{ color: '#ff7a45', fontSize: 14 }}>Manage Plans →</Link>
        </div>
        {plans.length === 0 ? (
          <p style={{ color: '#9ca3af' }}>No plans configured.</p>
        ) : (
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            {plans.map(p => {
              const dollars = (Number(p.priceCents) / 100).toFixed(2);
              const oneTime = p.tier === 'ad' || p.planId === 'voice_ad';
              const custom = p.tier === 'enterprise' && !Number(p.priceCents);
              const priceLabel = oneTime ? `$${dollars} once` : custom ? 'Custom' : `$${dollars}/mo`;
              const quotaLabel = oneTime
                ? 'Per ad, after the free one'
                : custom
                  ? 'Custom quota'
                  : `${p.monthlyQuotaMinutes} min/month`;
              return (
              <div key={p.planId} style={{ background: '#0f172a', border: '1px solid #374151', borderRadius: 8, padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{ color: '#e5e7eb', fontWeight: 500 }}>{p.name}</div>
                  {p.active && <span style={{ background: '#10b981', color: '#0b0b0e', padding: '2px 6px', borderRadius: 4, fontSize: 10 }}>Active</span>}
                </div>
                <div style={{ color: '#9ca3af', fontSize: 14 }}>{priceLabel}</div>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>{quotaLabel}</div>
                <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>Tier: {p.tier}</div>
              </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Admin Tools Grid */}
      <h3 style={{ color: '#e5e7eb', marginBottom: 16 }}>Admin Tools</h3>
      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
          <h4 style={{ marginTop: 0, color: '#e5e7eb' }}>Streams Moderation</h4>
          <p style={{ color: '#9ca3af', fontSize: 14 }}>Review and moderate user streams.</p>
          <Link to="/admin/streams-moderation" style={{ background: '#ff5a1f', color: '#fff', padding: '8px 12px', borderRadius: 8, textDecoration: 'none', display: 'inline-block' }}>Open Moderation</Link>
        </section>
        <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
          <h4 style={{ marginTop: 0, color: '#e5e7eb' }}>Uploads (Admin)</h4>
          <p style={{ color: '#9ca3af', fontSize: 14 }}>Browse and delete files in /uploads.</p>
          <Link to="/admin/uploads" style={{ background: '#ff5a1f', color: '#fff', padding: '8px 12px', borderRadius: 8, textDecoration: 'none', display: 'inline-block' }}>Open Uploads</Link>
        </section>
        <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
          <h4 style={{ marginTop: 0, color: '#e5e7eb' }}>Metrics</h4>
          <p style={{ color: '#9ca3af', fontSize: 14 }}>Key activity and system metrics.</p>
          <Link to="/admin/metrics" style={{ background: '#ff5a1f', color: '#fff', padding: '8px 12px', borderRadius: 8, textDecoration: 'none', display: 'inline-block' }}>Open Metrics</Link>
        </section>
        <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
          <h4 style={{ marginTop: 0, color: '#e5e7eb' }}>Settings</h4>
          <p style={{ color: '#9ca3af', fontSize: 14 }}>Quotas, metered endpoints, gates, and flags.</p>
          <Link to="/admin/settings" style={{ background: '#ff5a1f', color: '#fff', padding: '8px 12px', borderRadius: 8, textDecoration: 'none', display: 'inline-block' }}>Open Settings</Link>
        </section>
        <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
          <h4 style={{ marginTop: 0, color: '#e5e7eb' }}>Demo Ads</h4>
          <p style={{ color: '#9ca3af', fontSize: 14 }}>Generate demo ad audio for testing.</p>
          <Link to="/admin/demo-ads" style={{ background: '#ff5a1f', color: '#fff', padding: '8px 12px', borderRadius: 8, textDecoration: 'none', display: 'inline-block' }}>Open Demo Ads</Link>
        </section>
        <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
          <h4 style={{ marginTop: 0, color: '#e5e7eb' }}>System Analysis</h4>
          <p style={{ color: '#9ca3af', fontSize: 14 }}>View system health, metrics, and performance data.</p>
          <Link to="/admin/system" style={{ background: '#ff5a1f', color: '#fff', padding: '8px 12px', borderRadius: 8, textDecoration: 'none', display: 'inline-block' }}>Open System Analysis</Link>
        </section>
        <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
          <h4 style={{ marginTop: 0, color: '#e5e7eb' }}>Test Credits</h4>
          <p style={{ color: '#9ca3af', fontSize: 14 }}>Grant one-off credits to a user for QA and internal testing.</p>
          <form onSubmit={handleGrantCredits} style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            <label style={{ fontSize: 13, color: '#e5e7eb', display: 'grid', gap: 4 }}>
              User
              <select
                value={grantUserId}
                onChange={(e) => setGrantUserId(e.target.value)}
                style={{ padding: 6, borderRadius: 6, border: '1px solid #374151', background: '#020617', color: '#e5e7eb' }}
              >
                <option value="">Select user…</option>
                {users.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.email || u._id}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 13, color: '#e5e7eb', display: 'grid', gap: 4 }}>
              Credits to grant
              <input
                type="number"
                min={1}
                step={10}
                value={grantAmount}
                onChange={(e) => setGrantAmount(Number(e.target.value) || 0)}
                style={{ padding: 6, borderRadius: 6, border: '1px solid #374151', background: '#020617', color: '#e5e7eb' }}
              />
            </label>
            {grantError && <div style={{ color: '#fca5a5', fontSize: 12 }}>{grantError}</div>}
            {grantSuccess && <div style={{ color: '#6ee7b7', fontSize: 12 }}>{grantSuccess}</div>}
            <button
              type="submit"
              disabled={grantBusy}
              style={{
                marginTop: 4,
                background: '#ff5a1f',
                color: '#fff',
                padding: '8px 12px',
                borderRadius: 8,
                border: 0,
                cursor: grantBusy ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              {grantBusy ? 'Granting…' : 'Grant Credits'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
